// ────────────────────────────────────────────────────────────────────────────
// Bookings (Ferry) — presentation prototype mock data.
//
// FE-only seed for the Bookings screen, mirroring the Ferry Booking spec
// (myTMS — Ferry Booking Module). Nothing here is wired to the BE: the
// constraint engine, operator APIs, Azure port scoring, customs hooks and
// live capacity are all parked (see BookingsPage header). The shapes are
// deliberately UI-shaped (display strings, not raw domain) so the screen
// can be reacted to before the data model is designed.
// ────────────────────────────────────────────────────────────────────────────

import type { FerryBooking } from '@my/ferry-api';
import type { OrderBooking } from '@my/tms-api';

/** Cross-border corridor — drives the customs flag + cutoff rules. */
export type Corridor = 'IE→GB' | 'IE→EU' | 'NI→GB' | 'GB→IE' | 'GB→EU';

/** Sailing classification — rate + capacity behaviour (spec §2.1). */
export type SailingType = 'Off-peak' | 'Peak' | 'Shoulder';

/**
 * Row lifecycle state across the two queues.
 *  • Unbooked / Alert — To Book queue (no booking yet).
 *  • Proposed — a myI suggestion for an unbooked order (panel only, not a row status).
 *  • Booked — booking placed with the operator, awaiting confirmation (no reference yet).
 *  • Confirmed — operator confirmed and returned a booking reference.
 *  • Manual — booked outside the API (manual reference).
 */
export type BookingStatus = 'Unbooked' | 'Alert' | 'Proposed' | 'Booked' | 'Confirmed' | 'Manual';

/** Planning horizon day — Today / +1 / +2 (spec §4.2). */
export type HorizonDay = 'today' | 'plus1' | 'plus2';

/** A departure port a depot serves. */
export interface DeparturePort {
  id: string;
  /** Short label for the filter chip + queue, e.g. "Dublin Port". */
  label: string;
}

/** Dublin depot's ports (spec §4.2 — depot → ports mapping is config later). */
export const DEPARTURE_PORTS: readonly DeparturePort[] = [
  { id: 'dub', label: 'Dublin Port' },
  { id: 'ros', label: 'Rosslare' },
] as const;

/** Day offset from today for each horizon bucket — shared by the page filter
 *  and the booking journey (which needs the sailing's departure day). */
export const HORIZON_OFFSET: Record<HorizonDay, number> = { today: 0, plus1: 1, plus2: 2 };

/** The organisation's route pick an option was built from, as the booking needs it. */
export interface CandidateRoute {
  /** `OrganisationRoute.catalogueKey` — a catalogue-file key or a Ferry key. */
  catalogueKey: string;
  orgRouteId: string;
  /** The file entry sailed backwards; a Ferry entry is one direction. */
  reversed: boolean;
  /** Starred in Settings. */
  preferred: boolean;
}

/**
 * One sailing option for an order's ferry leg — the recommended route plus
 * any ranked alternatives. `roadKm`/`cost` are the arrival-port-to-delivery
 * leg (spec §3.3 port optimisation); `sailTime` is set once a sailing is
 * selected/booked.
 */
export interface RouteOption {
  /** Route shorthand, e.g. "Ross→Pemb". */
  routeShort: string;
  /** Departure port name, e.g. "Rosslare" — drives the Port filter chips. */
  departurePort?: string;
  /** Arrival port name, e.g. "Pembroke". */
  arrivalPort: string;
  /** Operating ferry company, e.g. "Irish Ferries". */
  operator: string;
  /** Road km from the arrival port to the delivery destination. */
  roadKm: number;
  /**
   * Routed truck driving minutes for that leg, when a routing service has
   * answered for it. Absent means "fall back to the straight-line estimate and
   * SAY SO" — a surface must not present the two as the same thing.
   */
  roadMinutes?: number;
  /** True while `roadKm` is a straight-line estimate rather than a routed distance. */
  roadEstimated?: boolean;
  /** Indicative sailing cost (EUR). */
  cost: number;
  type: SailingType;
  /** Scheduled departure, e.g. "20:30" — set on proposed/booked rows. */
  sailTime?: string;
  /** The pick behind a real candidate; absent on a booked row. */
  candidate?: CandidateRoute;
}

/** A queue row — an order with a ferry leg in the horizon. */
export interface BookingRow {
  id: string;
  orderNumber: string;
  customer: string;
  /** Cargo headline, e.g. "GDP Refrig" / "Hazardous" / "Full Load". */
  cargoLabel: string;
  /** Delivery destination town, e.g. "Birmingham". */
  destination: string;
  corridor: Corridor;
  /** Whether the corridor needs customs (drives the cutoff window + flag). */
  customs: boolean;
  /** Delivery window label — "07:00" (fixed/AM-PM) or "Any". */
  window: string;
  status: BookingStatus;
  /** Ferry operator's booking reference — present once Confirmed; null while only Booked. */
  bookingRef?: string | null;
  /** Recommended route — undefined when no sailing fits (Alert). */
  best?: RouteOption;
  /** Ranked alternatives (the "+N alt" affordance). */
  alts: RouteOption[];
  /** Why no sailing fits — set on Alert rows. */
  noFitReason?: string;
  /** Minutes until the port cutoff; null = already missed; undefined = not computed (cutoff engine parked). */
  cutoffMinutes?: number | null;
  horizon: HorizonDay;
  /** Required delivery day as an offset from today (0 = today). */
  deliveryDayOffset: number;
  /** Departure port id (for the port filter). */
  portId: string;
  /** The TMS booking behind a booked row. */
  booking?: OrderBooking | null;
  /** The carrier's side of that booking from My.Ferry, joined by externalReference; null when not booked through Ferry. */
  ferry?: FerryBooking | null;
  /** From the order's service tags, for the carrier's dangerous-goods flag. */
  hazardous?: boolean;
  /** The cargo's temperature setting; null when not temperature-controlled. */
  temperatureC?: number | null;
  /** The cargo's equipment keys, for the carrier's vehicle type. */
  equipmentKeys?: string[];
  /** Container cargo books as a container type, everything else as an artic. */
  containerCargo?: boolean;
}

/** A myI sailing proposal awaiting dispatcher confirmation (spec §4.5/2). */
export interface BookingProposal {
  id: string;
  orderNumber: string;
  customer: string;
  routeShort: string;
  sailTime: string;
  operator: string;
  type: SailingType;
  roadKm: number;
  destination: string;
  /** Reasoning note — why this sailing was selected. */
  note: string;
  confirmed: boolean;
}

/** A myI exception — no sailing meets the constraint (spec §4.5/3). */
export interface BookingException {
  id: string;
  orderNumber: string;
  customer: string;
  corridor: Corridor;
  cargoLabel: string;
  window: string;
  /** Specific reason, e.g. "Earliest arr 09:30, misses by 2h 30m". */
  reason: string;
}

/** Per-route capacity forecast across the horizon (spec §4.5/4). */
export interface CapacityForecastRow {
  operator: string;
  routeShort: string;
  /** Units needed across the horizon. */
  needed: number;
  offPeakBlock: number;
  offPeakOpen: number;
  peakBlock: number;
  peakOpen: number;
}

/**
 * Capacity swap — the "clever" suggestion. When a reservation block is full and
 * an urgent order needs a slot today, myI finds an already-booked order on the
 * same block whose delivery has slack (can move to a later sailing) and proposes
 * bumping it to free the slot for the urgent one. (Reshuffle, not just propose.)
 */
export interface SwapSuggestion {
  id: string;
  route: string; // 'Ross→Pemb'
  operator: string;
  /** Why the block is the constraint, e.g. "Off-peak block full · 2/2". */
  blockNote: string;
  /** The order that needs the freed slot now. */
  urgent: { orderNumber: string; customer: string; need: string };
  /** The booked order with delivery slack that can move. */
  bump: { orderNumber: string; customer: string; slack: string; moveTo: string };
  /** One-line rationale for the swap. */
  rationale: string;
}

// ── Seed rows ────────────────────────────────────────────────────────────────

export const TO_BOOK_ROWS: BookingRow[] = [
  {
    id: 'tb1',
    orderNumber: '2606-0118',
    customer: 'Murphy Transport',
    cargoLabel: 'GDP Refrig',
    destination: 'Birmingham',
    corridor: 'IE→GB',
    customs: true,
    window: '07:00',
    status: 'Alert',
    alts: [],
    noFitReason: 'No sailing fits window',
    cutoffMinutes: null,
    horizon: 'today',
    deliveryDayOffset: 0,
    portId: 'dub',
  },
  {
    id: 'tb2',
    orderNumber: '2606-0127',
    customer: 'Norwood Logistics',
    cargoLabel: 'Standard',
    destination: 'Cardiff',
    corridor: 'IE→GB',
    customs: false,
    window: 'Any',
    status: 'Unbooked',
    best: {
      routeShort: 'Ross→Pemb',
      arrivalPort: 'Pembroke',
      operator: 'Irish Ferries',
      roadKm: 38,
      cost: 180,
      type: 'Off-peak',
    },
    alts: [
      {
        routeShort: 'Dub→Liv',
        arrivalPort: 'Liverpool',
        operator: 'P&O',
        roadKm: 254,
        cost: 240,
        type: 'Off-peak',
      },
    ],
    cutoffMinutes: 620,
    horizon: 'today',
    deliveryDayOffset: 1,
    portId: 'ros',
  },
  {
    id: 'tb3',
    orderNumber: '2606-0119',
    customer: 'Brennan Haulage',
    cargoLabel: 'Hazardous',
    destination: 'Lyon',
    corridor: 'IE→EU',
    customs: true,
    window: 'Any',
    status: 'Unbooked',
    best: {
      routeShort: 'Ross→Cher',
      arrivalPort: 'Cherbourg',
      operator: 'Brittany',
      roadKm: 480,
      cost: 406,
      type: 'Off-peak',
    },
    alts: [],
    cutoffMinutes: 580,
    horizon: 'today',
    deliveryDayOffset: 2,
    portId: 'ros',
  },
  {
    id: 'tb4',
    orderNumber: '2606-0162',
    customer: 'Fastway Couriers',
    cargoLabel: 'Standard',
    destination: 'Leeds',
    corridor: 'IE→GB',
    customs: true,
    window: 'Any',
    status: 'Unbooked',
    best: {
      routeShort: 'Dub→Liv',
      arrivalPort: 'Liverpool',
      operator: 'P&O',
      roadKm: 121,
      cost: 240,
      type: 'Off-peak',
    },
    alts: [
      {
        routeShort: 'Dub→Holy',
        arrivalPort: 'Holyhead',
        operator: 'Irish Ferries',
        roadKm: 215,
        cost: 205,
        type: 'Off-peak',
      },
    ],
    cutoffMinutes: 1680,
    horizon: 'plus1',
    deliveryDayOffset: 2,
    portId: 'dub',
  },
];

export const BOOKED_ROWS: BookingRow[] = [
  {
    id: 'bk1',
    orderNumber: '2606-0114',
    customer: 'Kingspan Group',
    cargoLabel: 'Full Load',
    destination: 'Bristol',
    corridor: 'IE→GB',
    customs: true,
    window: '06:00',
    status: 'Confirmed',
    bookingRef: 'IF-22841',
    best: {
      routeShort: 'Ross→Pemb',
      arrivalPort: 'Pembroke',
      operator: 'Irish Ferries',
      roadKm: 68,
      cost: 195,
      type: 'Off-peak',
      sailTime: '20:30',
    },
    alts: [
      {
        routeShort: 'Dub→Liv',
        arrivalPort: 'Liverpool',
        operator: 'P&O',
        roadKm: 290,
        cost: 250,
        type: 'Off-peak',
      },
    ],
    cutoffMinutes: 310,
    horizon: 'today',
    deliveryDayOffset: 1,
    portId: 'ros',
  },
  {
    id: 'bk2',
    orderNumber: '2606-0110',
    customer: 'Ornua',
    cargoLabel: 'GDP Refrig',
    destination: 'Paris',
    corridor: 'IE→EU',
    customs: true,
    window: '14:30',
    status: 'Confirmed',
    bookingRef: 'IF-22907',
    best: {
      routeShort: 'Ross→Cher',
      arrivalPort: 'Cherbourg',
      operator: 'Irish Ferries',
      roadKm: 360,
      cost: 398,
      type: 'Off-peak',
      sailTime: '17:00',
    },
    alts: [
      {
        routeShort: 'Ross→Rosc',
        arrivalPort: 'Roscoff',
        operator: 'Brittany',
        roadKm: 410,
        cost: 372,
        type: 'Off-peak',
      },
    ],
    cutoffMinutes: 480,
    horizon: 'today',
    deliveryDayOffset: 2,
    portId: 'ros',
  },
  {
    id: 'bk3',
    orderNumber: '2606-0133',
    customer: 'Ace Haulage',
    cargoLabel: 'Full Load',
    destination: 'Manchester',
    corridor: 'IE→GB',
    customs: true,
    window: 'Any',
    status: 'Confirmed',
    best: {
      routeShort: 'Dub→Liv',
      arrivalPort: 'Liverpool',
      operator: 'P&O',
      roadKm: 34,
      cost: 274,
      type: 'Peak',
      sailTime: '22:00',
    },
    alts: [
      {
        routeShort: 'Dub→Holy',
        arrivalPort: 'Holyhead',
        operator: 'Irish Ferries',
        roadKm: 245,
        cost: 210,
        type: 'Off-peak',
      },
    ],
    bookingRef: 'PO-55304',
    cutoffMinutes: 540,
    horizon: 'today',
    deliveryDayOffset: 1,
    portId: 'dub',
  },
  {
    id: 'bk4',
    orderNumber: '2606-0141',
    customer: 'Kellys Freight',
    cargoLabel: 'Temperature',
    destination: 'Liverpool',
    corridor: 'IE→GB',
    customs: true,
    window: '08:00',
    status: 'Booked',
    best: {
      routeShort: 'Dub→Liv',
      arrivalPort: 'Liverpool',
      operator: 'P&O',
      roadKm: 8,
      cost: 226,
      type: 'Shoulder',
      sailTime: '21:00',
    },
    alts: [
      {
        routeShort: 'Dub→Holy',
        arrivalPort: 'Holyhead',
        operator: 'Irish Ferries',
        roadKm: 178,
        cost: 200,
        type: 'Off-peak',
      },
    ],
    cutoffMinutes: 490,
    horizon: 'today',
    deliveryDayOffset: 1,
    portId: 'dub',
  },
  {
    id: 'bk5',
    orderNumber: '2606-0155',
    customer: 'DHL Freight IE',
    cargoLabel: 'Standard',
    destination: 'Swansea',
    corridor: 'IE→GB',
    customs: false,
    window: 'Any',
    status: 'Confirmed',
    best: {
      routeShort: 'Ross→Pemb',
      arrivalPort: 'Pembroke',
      operator: 'Stena',
      roadKm: 22,
      cost: 177,
      type: 'Off-peak',
      sailTime: '08:30',
    },
    alts: [
      {
        routeShort: 'Ross→Fish',
        arrivalPort: 'Fishguard',
        operator: 'Stena',
        roadKm: 40,
        cost: 170,
        type: 'Off-peak',
      },
    ],
    bookingRef: 'ST-11806',
    cutoffMinutes: 720,
    horizon: 'today',
    deliveryDayOffset: 3,
    portId: 'ros',
  },
  {
    id: 'bk6',
    orderNumber: '2606-0170',
    customer: 'Coillte',
    cargoLabel: 'Full Load',
    destination: 'Birmingham',
    corridor: 'IE→GB',
    customs: true,
    window: 'Any',
    status: 'Booked',
    best: {
      routeShort: 'Dub→Holy',
      arrivalPort: 'Holyhead',
      operator: 'Irish Ferries',
      roadKm: 290,
      cost: 215,
      type: 'Off-peak',
      sailTime: '02:45',
    },
    alts: [],
    cutoffMinutes: 1500,
    horizon: 'plus1',
    deliveryDayOffset: 2,
    portId: 'dub',
  },
];

// ── myI panel seed ───────────────────────────────────────────────────────────

export const PROPOSALS: BookingProposal[] = [
  {
    id: 'p2',
    orderNumber: '2606-0119',
    customer: 'Brennan Haulage',
    routeShort: 'Ross→Cher',
    sailTime: '17:00',
    operator: 'Brittany',
    type: 'Off-peak',
    roadKm: 480,
    destination: 'Lyon',
    note: 'Hazardous — DGN forward required before cutoff. Block covers.',
    confirmed: false,
  },
];

export const EXCEPTIONS: BookingException[] = [
  {
    id: 'e1',
    orderNumber: '2606-0118',
    customer: 'Murphy Transport',
    corridor: 'IE→GB',
    cargoLabel: 'GDP Refrig',
    window: '07:00',
    reason:
      'No sailing meets the 07:00 delivery window from Dublin. Earliest Dub→Holy arrival 09:30 — misses by 2h 30m. Manual decision needed.',
  },
];

export const CAPACITY_FORECAST: CapacityForecastRow[] = [
  {
    operator: 'Irish Ferries',
    routeShort: 'Ross→Pemb',
    needed: 3,
    offPeakBlock: 0,
    offPeakOpen: 0,
    peakBlock: 0,
    peakOpen: 4,
  },
  {
    operator: 'Brittany',
    routeShort: 'Ross→Cher',
    needed: 2,
    offPeakBlock: 3,
    offPeakOpen: 5,
    peakBlock: 0,
    peakOpen: 3,
  },
  {
    operator: 'P&O',
    routeShort: 'Dub→Liv',
    needed: 5,
    offPeakBlock: 1,
    offPeakOpen: 2,
    peakBlock: 2,
    peakOpen: 4,
  },
];

export const SWAP_SUGGESTIONS: SwapSuggestion[] = [
  {
    id: 'sw1',
    route: 'Ross→Pemb',
    operator: 'Irish Ferries',
    blockNote: 'Off-peak block full · 2/2',
    urgent: {
      orderNumber: '2606-0127',
      customer: 'Norwood Logistics',
      need: 'Delivers Thu 25 Jun — must sail today; off-peak block full',
    },
    bump: {
      orderNumber: '2606-0155',
      customer: 'DHL Freight IE',
      slack: 'Delivers Sat 27 Jun · 2 days slack',
      moveTo: 'tomorrow’s off-peak sailing — still arrives in time',
    },
    rationale:
      'DHL holds an off-peak block slot but doesn’t deliver until Sat 27 Jun, so a later sailing still works. Bump it to free today’s slot for Norwood (delivers Thu 25 Jun) — both stay at the contracted off-peak rate.',
  },
];
